import type { ReactNode } from 'react';

import { useTranslation } from '@fluxora/i18n';
import { Button, Card, CardBody, Spinner } from '@fluxora/ui';

import { useElapsedMs } from '../hooks/useElapsedMs';
import { acceptProgressKey } from '../services/sign-completion.service';

/**
 * ── THE SIGNING PANEL — WHERE THE CUSTOMER ACTUALLY SIGNS ──────────────────
 *
 * The drawn-mark pad and the primary action, presented as ONE titled, visually
 * distinct block at the end of the step.
 *
 * ── WHY THIS COMPONENT EXISTS ──────────────────────────────────────────────
 * The pad, the sign button, the blocked-reason sentence, the progress spinner and
 * the submit error were five loose siblings at the bottom of a single flat column,
 * styled exactly like the six disclosure blocks above them. Measured on a
 * 1375×1750 viewport: the canvas sat at y≈3652 on a 4,263px page, ~2,900px below
 * the contract it accompanies and visually indistinguishable from the deposit
 * notices it followed. A customer reported three times that they could not see it.
 *
 * Nothing here MOVED the act of signing earlier — the customer asked for it to stay
 * at the end — but at the end it now reads as an arrival rather than as another
 * paragraph: an accent-bordered panel with its own heading, holding the mark and
 * the button together so "draw" and "sign" are one step instead of two things that
 * happen to be adjacent.
 *
 * ── ⚠️ THE PAD IS NOW A GATE. THIS REVERSES E-SF17 / ADR-0010 D5.6. ────────
 * The paragraph that used to sit here said the opposite — that the button is never
 * disabled for want of a drawing and the panel is completable with no pointer input
 * at all. That is no longer true, and the change was made deliberately and from
 * above ("sign on contract should be compulsory, not an option").
 *
 * Two things did NOT change, and conflating them with the reversal is the mistake
 * to avoid here. The typed name captured at the credit step plus the three
 * acknowledgements are STILL the operative ESIGN mark, and the drawing STILL only
 * corroborates. Nothing was relaxed to make room for the new requirement; a
 * requirement was added on top.
 *
 * ⚠️ THE COST IS REAL AND IS RECORDED RATHER THAN SOFTENED: this panel can no
 * longer be completed without a pointer, so keyboard-only and screen-reader
 * customers cannot self-enroll on this surface. `SignaturePad`'s status line names
 * the assisted route for them. The honest fix — a customer-elected, RECORDED
 * accommodation rather than a silent bypass — needs a field on
 * `SignatureEventSchema` carried through the BFF to Quotation, and is not
 * shippable from this repo alone. See `SignGateInput.signatureDrawn`.
 *
 * ── THE WAIT IS NAMED (measured 13.9 s and 25.4 s) ─────────────────────────
 * `POST /bff/sign/accept` runs three upstream hops. While it is in flight this
 * panel replaces the button with a NAMED, escalating progress block that says what
 * is happening and tells the customer not to close the page — because the previous
 * treatment (a changed button label and a small spinner) over a 14-to-25-second
 * wait is indistinguishable from a dead page, on the one action a customer will
 * press twice if they think it did not land.
 */
export interface SigningPanelProps {
  /** The drawn-mark pad. Passed in so this panel owns LAYOUT, not canvas mechanics. */
  readonly signaturePad: ReactNode;
  /** Whether the primary action can be pressed. */
  readonly canSign: boolean;
  /** i18n key explaining why it cannot be, or undefined when it can. */
  readonly blockedReasonKey: string | undefined;
  /**
   * True when the consents are incomplete. Deliberately does NOT disable the
   * button — see `agreement-preview.service.signBlockedReasonKey`.
   */
  readonly consentIncomplete: boolean;
  readonly submitting: boolean;
  readonly onSign: () => void;
  /** The submit failure, already rendered as a title + body by the caller. */
  readonly error: ReactNode;
}

export function SigningPanel({
  signaturePad,
  canSign,
  blockedReasonKey,
  consentIncomplete,
  submitting,
  onSign,
  error,
}: SigningPanelProps): JSX.Element {
  const { t } = useTranslation();
  // Timed from the moment THIS submit started, so a retry is timed from the retry.
  const elapsedMs = useElapsedMs(submitting);

  return (
    <Card
      variant="glass"
      padding="md"
      // The accent ring is the whole point: every other block on this step is a
      // plain-bordered card, so the one the customer has to act in must not be.
      className="border-2 border-primary/50 shadow-float"
      data-testid="signing-panel"
    >
      <CardBody className="flex flex-col gap-4 p-0">
        <div className="flex flex-col gap-1">
          <h2 className="text-lg font-bold text-primary">{t('shop.sign.panel.heading')}</h2>
          <p className="text-sm text-foreground/70">{t('shop.sign.panel.intro')}</p>
        </div>

        {signaturePad}

        {/* ── THE SIGN CONTROL ──────────────────────────────────────────────
            It is genuinely `disabled` when blocked — not merely styled as such —
            so assistive technology reports it as unavailable. QA found a primary
            button reporting `disabled === false` while being inert; a control that
            lies about whether it can be pressed is worse than one that cannot be.

            The REASON is always stated in text below (`role="status"`, and
            referenced by `aria-describedby`), because a greyed-out button tells a
            customer they are stuck without telling them why. Both the disabled
            state and the sentence come from ONE computation
            (`agreement-preview.service`), so they cannot drift.

            ⚠️ MISSING CONSENTS DELIBERATELY LEAVE IT LIVE. Pressing it then
            surfaces a per-checkbox error pointing at the box that is missing —
            more useful than one vague refusal, and it keeps the §25.475
            acceptances in front of a correction rather than behind an inert
            control. */}
        {submitting ? (
          <div
            className="flex flex-col items-center gap-3 rounded-md border border-primary/30 bg-primary/5 px-4 py-6"
            role="status"
            aria-live="polite"
            data-testid="sign-progress"
          >
            {/* ⚠️ THE SPINNER IS HIDDEN FROM ASSISTIVE TECH, DELIBERATELY — the
                same rule `ContractPreview` documents. `Spinner` carries its own
                `role="status"` and an sr-only label, so leaving it visible would
                put TWO live regions in this one block and a screen reader would
                hear the fixed label competing with the escalating sentence. The
                wrapper owns the announcement; the spinner is decoration. */}
            <span aria-hidden="true">
              <Spinner size="lg" label="" />
            </span>
            <p className="text-center text-sm font-medium text-foreground">
              {t(acceptProgressKey(elapsedMs))}
            </p>
            <p className="text-center text-xs text-foreground/60">
              {t('shop.sign.submit.progress.doNotClose')}
            </p>
          </div>
        ) : (
          <Button
            variant="primary"
            shape="pill"
            size="lg"
            fullWidth
            onClick={onSign}
            disabled={!canSign}
            {...(blockedReasonKey === undefined
              ? {}
              : { 'aria-describedby': 'sign-blocked-reason' })}
          >
            {t('shop.sign.submit.cta')}
          </Button>
        )}

        {blockedReasonKey !== undefined ? (
          <span id="sign-blocked-reason" role="status" className="text-xs text-foreground/70">
            {t(blockedReasonKey)}
          </span>
        ) : consentIncomplete && !submitting ? (
          <span className="text-xs text-foreground/60">{t('shop.sign.submit.consentHint')}</span>
        ) : null}

        {error}
      </CardBody>
    </Card>
  );
}
